import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/api';
import { useNotification } from '../../hooks/useNotification';
import BrandHomeLink from '../common/BrandHomeLink';
import './AuthPages.css';

export default function ForgotPassword() {
  const { t } = useTranslation('auth');
  const [step, setStep] = useState(1); // 1=email, 2=otp+newpass
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { show, NotificationBar } = useNotification();

  const sendOtp = async e => {
    e.preventDefault();
    if (!email) { show(t('forgotPassword.enterEmail'), 'error'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password/send-otp', { email });
      if (res.data.success) { show(t('forgotPassword.otpSent'), 'success'); setStep(2); }
      else show(res.data.message, 'error');
    } catch { show(t('forgotPassword.sendOtpFailed'), 'error'); }
    finally { setLoading(false); }
  };

  const resetPassword = async e => {
    e.preventDefault();
    if (!otp || !newPassword) { show(t('forgotPassword.fillAllFields'), 'error'); return; }
    if (newPassword !== confirm) { show(t('forgotPassword.passwordsMismatch'), 'error'); return; }
    if (newPassword.length < 6) { show(t('forgotPassword.passwordMinLength'), 'error'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password/reset', { email, otp, newPassword });
      if (res.data.success) {
        show(t('forgotPassword.resetSuccess'), 'success');
        setTimeout(() => navigate('/products/order-tracking/login'), 2000);
      } else show(res.data.message, 'error');
    } catch { show(t('forgotPassword.resetFailed'), 'error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Reset Password | EffyVenture Order Tracking</title>
        <meta name="description" content="Forgot your EffyVenture password? Enter your email to receive a one-time password and regain access to your account." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://www.effyventure.com/products/order-tracking/forgot-password" />
      </Helmet>
      <NotificationBar />
      <div className="auth-card shine-rail">
        <div className="auth-card__brand">
          <BrandHomeLink className="auth-brand-logo">
            <img src="/logo/logo-icon.svg" alt="EffyVenture" className="auth-brand-logo__img" />
            <span className="auth-brand-logo__text">
              <span className="auth-brand-logo__effy">Effy</span><span className="auth-brand-logo__venture">Venture</span>
            </span>
          </BrandHomeLink>
          <span className="auth-card__product">{t('forgotPassword.productLabel')}</span>
        </div>
        <span className="eyebrow" style={{ marginBottom: 10 }}>{step === 1 ? t('forgotPassword.eyebrowRecovery') : t('forgotPassword.eyebrowVerification')}</span>
        <h1 className="auth-card__title">
          {step === 1 ? t('forgotPassword.titleReset') : t('forgotPassword.titleOtp')}
        </h1>
        <p className="auth-card__subtitle">
          {step === 1 ? t('forgotPassword.subtitleStep1') : t('forgotPassword.subtitleStep2', { email })}
        </p>

        {step === 1 ? (
          <form onSubmit={sendOtp}>
            <div className="form-group">
              <label className="form-label">{t('forgotPassword.emailAddress')}</label>
              <input className="input-field" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('forgotPassword.emailPlaceholder')} autoFocus />
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? <><span className="spinner" /> {t('forgotPassword.sendingOtp')}</> : t('forgotPassword.sendOtp')}
            </button>
          </form>
        ) : (
          <form onSubmit={resetPassword}>
            <div className="form-group">
              <label className="form-label">{t('forgotPassword.otpCode')}</label>
              <input className="input-field" value={otp} onChange={e => setOtp(e.target.value)} placeholder={t('forgotPassword.otpPlaceholder')} maxLength={6} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">{t('forgotPassword.newPassword')}</label>
              <input className="input-field" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder={t('forgotPassword.newPasswordPlaceholder')} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('forgotPassword.confirmPassword')}</label>
              <input className="input-field" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t('forgotPassword.confirmPasswordPlaceholder')} />
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? <><span className="spinner" /> {t('forgotPassword.resetting')}</> : t('forgotPassword.resetPassword')}
            </button>
            <button type="button" className="btn btn-secondary auth-submit" style={{ marginTop: 8 }} onClick={() => setStep(1)}>
              {t('forgotPassword.back')}
            </button>
          </form>
        )}
        <p className="auth-card__switch">
          {t('forgotPassword.rememberPassword')} <Link to="/products/order-tracking/login">{t('forgotPassword.signIn')}</Link>
        </p>
      </div>
    </div>
  );
}
